import React, {FC, PropsWithChildren, useState} from 'react';
import styled, {css} from 'styled-components';

import {blendColors} from '../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fonts, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {ThemeContextType} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface ButtonProps {
  color: 'light' | 'dark';
  background: string;
  onClick: () => void;
  isDisabled?: boolean;
}

/*
 * Style.
 */

interface StyledButtonDivProps {
  $isDisabled?: boolean;
  $color: 'light' | 'dark';
  $background: string;
  $isFocused: boolean;
}

const StyledButtonDiv = styled.div<StyledButtonDivProps>`
  font-family: ${fonts.system};
  font-weight: ${fontWeights.semibold};
  font-size: 14px;
  line-height: 17px;
  padding: 6px 14px 7px 14px;

  border-radius: 100px;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  cursor: ${(p) => (p.$isDisabled ? 'default' : 'pointer')};

  ${(p) => boxShadow(p.theme, `0 1px 3px 0 ${p.theme.alphas.black40}`)};
  ${(p) => handleColors(p)}

  ${(p) => handleHover(p)}
`;

function handleColors(props: StyledButtonDivProps & ThemeContextType) {
  if (props.$isDisabled) {
    return css`
      color: ${props.theme.greys.shade70};
      background-color: ${props.theme.greys.shade40};
    `;
  }

  return css`
    color: ${props.$color === 'light' ? props.theme.invertedText : props.theme.text};
    background-color: ${props.$background};
  `;
}

function handleHover(props: StyledButtonDivProps & ThemeContextType) {
  if (props.$isDisabled) {
    return css``;
  }

  if (props.$isFocused) {
    return css`
      background-color: ${(p) => blendColors(props.$background, p.theme.alphas.black10)};
    `;
  }

  return css`
    &:hover {
      background-color: ${(p) => blendColors(props.$background, p.theme.alphas.black50)};
    }
  `;
}

/*
 * Component.
 */

export const Button: FC<PropsWithChildren<ButtonProps>> = (props) => {
  const [isFocused, setIsFocused] = useState(false);

  const onClick = () => {
    if (!props.isDisabled) {
      props.onClick();
    }
  };

  return (
    <ChromelessButton onClick={onClick} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}>
      <StyledButtonDiv
        $isDisabled={props.isDisabled}
        $color={props.color}
        $background={props.background}
        $isFocused={isFocused}
      >
        {props.children}
      </StyledButtonDiv>
    </ChromelessButton>
  );
};
